import { getAccessToken } from '@/api/auth';

export type ShareLinkTargetDto = {
  id: string;
  displayName: string | null;
  phoneNumber: string | null;
  userName: string | null;
};

export type ShareLinkTargetLookupDto = {
  user: ShareLinkTargetDto | null;
  existsButNotShareLinkTarget: boolean;
};

export class CreateShareLinkTargetError extends Error {
  readonly status: number;
  readonly responseBody: string;

  constructor(status: number, responseBody: string) {
    super(`Creating the share-link target failed (HTTP ${status}).`);
    this.name = 'CreateShareLinkTargetError';
    this.status = status;
    this.responseBody = responseBody;
  }
}

async function authorizationHeaders(): Promise<Record<string, string>> {
  const token = await getAccessToken();
  return token !== null ? { Authorization: `Bearer ${token}` } : {};
}

export async function getShareLinkTargets(identityServerUrl: string): Promise<ShareLinkTargetDto[]> {
  const response = await fetch(`${identityServerUrl}/Account/ShareLinkTargets`, {
    headers: await authorizationHeaders(),
  });
  if (!response.ok) {
    throw new Error(`Loading share-link targets failed (HTTP ${response.status})`);
  }

  return ((await response.json()) as ShareLinkTargetDto[] | null) ?? [];
}

export async function findShareLinkTargetByPhone(
  identityServerUrl: string,
  phoneNumber: string,
): Promise<ShareLinkTargetLookupDto> {
  const response = await fetch(
    `${identityServerUrl}/Account/ShareLinkTargets/by-phone?phone=${encodeURIComponent(phoneNumber)}`,
    { headers: await authorizationHeaders() },
  );
  if (!response.ok) {
    throw new Error(`Share-link target lookup failed (HTTP ${response.status})`);
  }

  return (
    ((await response.json()) as ShareLinkTargetLookupDto | null) ?? {
      user: null,
      existsButNotShareLinkTarget: false,
    }
  );
}

export async function createShareLinkTarget(
  identityServerUrl: string,
  phoneNumber: string,
  displayName: string,
): Promise<ShareLinkTargetDto> {
  const response = await fetch(`${identityServerUrl}/Account/ShareLinkTargets`, {
    method: 'POST',
    headers: { 'Content-Type': 'application/json', ...(await authorizationHeaders()) },
    body: JSON.stringify({ phoneNumber, displayName }),
  });

  if (!response.ok) {
    throw new CreateShareLinkTargetError(response.status, await response.text());
  }

  const created = (await response.json()) as ShareLinkTargetDto | null;
  if (created === null) {
    throw new Error('No share-link target returned from API.');
  }

  return created;
}

export async function createIncidentShareLink(
  identityServerUrl: string,
  incidentId: string,
  userId: string,
): Promise<string> {
  const response = await fetch(
    `${identityServerUrl}/Account/CreateIncidentShareLink/${incidentId}/for-user/${userId}`,
    { headers: await authorizationHeaders() },
  );
  if (!response.ok) {
    throw new Error(`Creating the incident share link failed (HTTP ${response.status})`);
  }

  return response.text();
}
